"use client";

import Link from "next/link";
import { SignedOut, useClerk } from "@clerk/nextjs";
import { CheckCircle2, Coffee, Download, RotateCcw, Scissors, UserPlus } from "lucide-react";
import { useProStatus } from "@/hooks/use-pro-status";
import { KOFI_URL } from "@/lib/kofi";

export function DownloadConfirmation({
  title = "Your download is ready",
  fileName,
  onDownloadAgain,
  onReset,
  resetLabel = "Convert another file",
  // Overridable so a caller embedding this alongside another card (rather
  // than centering it on its own full-page screen) can stretch it to match
  // instead of sizing to content.
  className = "flex-1 flex flex-col justify-center px-6 py-12 max-w-3xl mx-auto w-full",
  boxClassName = "rounded-xl p-8 glass-panel flex flex-col items-center text-center gap-6",
}: {
  title?: string;
  fileName?: string | null;
  onDownloadAgain: () => void;
  onReset: () => void;
  resetLabel?: string;
  className?: string;
  boxClassName?: string;
}) {
  const isPro = useProStatus();
  const { openSignUp } = useClerk();

  return (
    <div className={className}>
    <div className={boxClassName}>
      <div className="flex flex-col items-center gap-3">
        <div className="size-16 rounded-full bg-primary/10 flex items-center justify-center text-primary">
          <CheckCircle2 className="size-9" />
        </div>
        <h2 className="text-2xl font-black tracking-tight">{title}</h2>
        <p className="text-sm text-muted-foreground max-w-md">
          {fileName ? (
            <>
              <span className="font-medium text-foreground">{fileName}</span> should be in your
              Downloads folder.
            </>
          ) : (
            "Your file should be in your Downloads folder."
          )}{" "}
          Didn&apos;t get it?{" "}
          <button
            type="button"
            onClick={onDownloadAgain}
            className="font-medium text-primary hover:underline"
          >
            Download again
          </button>
          .
        </p>
      </div>

      <div className="w-full max-w-md flex flex-col gap-3">
        {/* Buy me a coffee — always shown */}
        <a
          href={KOFI_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-xl p-4 glass-panel border border-primary/10 flex items-center gap-4 text-left transition hover:border-primary/30"
        >
          <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
            <Coffee className="size-5 text-primary" />
          </div>
          <p className="flex-1 min-w-0 text-sm">
            If this saved you time, consider{" "}
            <span className="font-bold">buying me a coffee</span>.
          </p>
        </a>

        {/* Signed-out: free account unlocks unlimited conversions */}
        <SignedOut>
          <button
            type="button"
            onClick={() => openSignUp()}
            className="rounded-xl p-4 glass-panel border border-primary/10 flex items-center gap-4 text-left transition hover:border-primary/30"
          >
            <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
              <UserPlus className="size-5 text-primary" />
            </div>
            <p className="flex-1 min-w-0 text-sm">
              <span className="font-bold">Sign up free</span> to convert unlimited files.
            </p>
          </button>
        </SignedOut>

        {/* Non-Pro: upgrade unlocks trimming + batch */}
        {!isPro && (
          <Link
            href="/pricing"
            className="rounded-xl p-4 glass-panel border border-primary/10 flex items-center gap-4 text-left transition hover:border-primary/30"
          >
            <div className="p-2.5 bg-primary/10 rounded-lg shrink-0">
              <Scissors className="size-5 text-primary" />
            </div>
            <p className="flex-1 min-w-0 text-sm">
              <span className="font-bold">Upgrade to Pro</span> to trim your activities and convert
              files in batches.
            </p>
          </Link>
        )}
      </div>

      <div className="flex flex-wrap items-center justify-center gap-3">
        <button
          type="button"
          onClick={onDownloadAgain}
          className="inline-flex items-center gap-2 px-5 py-2.5 border border-border rounded-lg text-sm font-medium hover:border-muted-foreground transition-colors"
        >
          <Download className="size-4" /> Download again
        </button>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-primary-foreground rounded-lg text-sm font-bold hover:brightness-110 transition-all shadow-lg shadow-primary/20"
        >
          <RotateCcw className="size-4" /> {resetLabel}
        </button>
      </div>
    </div>
    </div>
  );
}
